/**
 * context/app/apiClient.js - how AppContext reads and writes: which lists a
 * person reads, the list GETs and project sends, and which lists a live
 * change re-reads (liveTargets). Moved out of AppContext.jsx, which
 * re-exports liveTargets.
 */
import { canSeeTab } from '../../data/roles';
import { latestReads } from '../../live/useLive';
import { ACCOUNT_COLUMNS, toCamel } from './rows';

const API_BASE = '';

/** The lists a person reads (a role, or their effective access). The API
    gate 403s the rest (engineers and crm-staff have no BD/financial access,
    etc.). */
const listsFor = (access) => ({
  projects: canSeeTab(access, 'projects') || canSeeTab(access, 'opportunities'),
  // Accounts and Leads are two views over the same crm_accounts rows.
  accounts: canSeeTab(access, 'crm') || canSeeTab(access, 'leads'),
  engineers: canSeeTab(access, 'engineers'),
});

/** GET /api/users/me: the caller's effective access, or null when it could not be read. */
const getMyAccess = () =>
  fetch(`${API_BASE}/api/users/me`)
    .then((r) => (r.ok ? r.json() : null))
    .then((a) => (a && !Array.isArray(a) && a.areas && typeof a.areas === 'object' ? a : null))
    .catch(() => null);

/** Two access answers give the same screens: same role, levels, switch and no-delete list. */
const sameAccess = (a, b) =>
  JSON.stringify(a && [a.role, a.areas, a.canManageAccess, a.noDelete]) ===
  JSON.stringify(b && [b.role, b.areas, b.canManageAccess, b.noDelete]);

/* A live re-read and a save's fetchAll can overlap. Each list shows the
   newest answer it has GOT (latestReads): an older answer that lands later
   never puts stale rows back - and a newer read that fails (a 502 while the
   API restarts) counts for nothing, so it can never get this tab's own
   re-read after a save thrown away, with the new row missing until the next
   change. A read that fails keeps what is on screen; it never blanks a list. */
const readers = () => ({ users: latestReads(), accounts: latestReads(), projects: latestReads(), engineers: latestReads() });

/** GET a list: its rows (camelCase), or null when it could not be read - a refused or failed answer is not an empty list. */
const getList = (path) =>
  fetch(`${API_BASE}${path}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((rows) => (Array.isArray(rows) ? toCamel(rows) : null))
    .catch(() => null);

/** One project save, answered whole: { ok, status, body } (body = parsed JSON or null). */
const sendProject = async (path, method, payload) => {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = await res.json().catch(() => null);
  return { ok: res.ok, status: res.status, body };
};

const LIVE_LISTS = ['users', 'accounts', 'projects', 'engineers'];

// middleware/audit.js names at most this many changed columns: a list that
// long may have left one out
const AUDIT_FIELD_CAP = 15;

/** Can this change of an opportunity row have changed an account? A new or
    deleted row can, and so can an edit whose changed columns are not all known. */
function movesAccounts(evt) {
  if (evt.method !== 'PUT' && evt.method !== 'PATCH') return true;
  const fields = Array.isArray(evt.fields) ? evt.fields : [];
  if (!fields.length || fields.length >= AUDIT_FIELD_CAP) return true;
  return fields.some((f) => ACCOUNT_COLUMNS.includes(f));
}

/**
 * What a live change (src/live/liveEvents.js) can have touched here: the
 * lists this context holds, plus 'comments' and 'docs' - the tables' comment
 * column and Doc Hub number links (hooks/useCommentsIndex, hooks/useDocIndex).
 * Kinds this context does not hold give [] and their own screens listen for
 * themselves. A resync (the connection came back after a drop) is everything.
 */
export function liveTargets(evt) {
  if (!evt) return [];
  if (evt.resync) return [...LIVE_LISTS, 'comments', 'docs'];
  switch (evt.kind) {
    case 'projects':
      if (evt.sub === 'comments') return ['comments'];
      if (evt.sub === 'attachments' || evt.sub === 'folders') return ['docs'];
      if (evt.sub) return [];
      // the row itself, and accounts when it can have moved one: an account
      // reads its Customer standing off the opportunities linked to it
      return movesAccounts(evt) ? ['projects', 'accounts'] : ['projects'];
    case 'attachments':
      return ['docs']; // a file relabeled, moved or deleted by its own id
    // deleting an account unlinks its opportunities (routes/accounts.js)
    case 'accounts':
      return evt.method === 'DELETE' ? ['accounts', 'projects'] : ['accounts'];
    case 'crm':
      return ['accounts']; // activity entries and their files
    case 'engineers':
      return ['engineers'];
    case 'manpower':
      return ['engineers']; // a calendar save rewrites the engineer cards
    case 'users':
      return ['users'];
    default:
      return [];
  }
}

export { API_BASE, listsFor, getMyAccess, sameAccess, readers, getList, sendProject, LIVE_LISTS };
